import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { AlarmClock, Plus, RefreshCw, Search } from 'lucide-react';
import { api } from '../../services/api';
import GreetedContactButtons from '../components/GreetedContactButtons';
import StatusPicker from '../components/StatusPicker';
import { formatAge, formatDateTime, formatMinutes, maskedPhone, snoozedUntil } from '../format';
import ManualLeadForm from '../components/ManualLeadForm';
import '../crm.css';

const PAGE_SIZE = 50;

export default function CrmLeadsPage() {
  const { t } = useTranslation('common');
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState(params.get('search') || '');

  // The filter lives in the URL so a reload, or a link pasted to the owner,
  // opens the same view of the queue.
  const view = params.get('view') || 'open';
  const setView = (next) => {
    const patch = new URLSearchParams(params);
    patch.set('view', next);
    patch.delete('page');
    setParams(patch);
  };
  const page = Number(params.get('page') || 0);
  const setPage = (next) => {
    const patch = new URLSearchParams(params);
    patch.set('page', String(next));
    setParams(patch);
  };

  const query = useMemo(() => {
    const request = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (view === 'open') request.open_only = true;
    if (view === 'overdue') request.awaiting_first_call = true;
    if (view !== 'open' && view !== 'overdue' && view !== 'all') request.status = view;
    if (params.get('search')) request.search = params.get('search');
    return request;
  }, [view, page, params]);

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      setData(await api.listCrmLeads(query));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [query]);

  useEffect(() => {
    api.getCrmMeta().then(setMeta).catch((e) => setError(e?.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitSearch = (event) => {
    event.preventDefault();
    const patch = new URLSearchParams(params);
    if (search.trim()) patch.set('search', search.trim());
    else patch.delete('search');
    patch.delete('page');
    setParams(patch);
  };

  const move = async (lead, body) => {
    try {
      await api.patchCrmLead(lead.id, body);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail?.error ? t(`crm.errors.${detail.error}`) : String(detail || e.message));
      return;
    }
    await load();
  };

  const counts = data?.counts_by_status || {};
  // The open view also holds everyone who said no and is due to be asked again.
  const openCount =
    (meta?.open_statuses || []).reduce((sum, status) => sum + (counts[status] || 0), 0) +
    (data?.come_back_due || 0);
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);

  const tabs = [
    { key: 'open', label: t('crm.tabs.open'), count: openCount },
    { key: 'overdue', label: t('crm.tabs.overdue'), count: data?.awaiting_first_call || 0, alarm: true },
    ...(meta?.statuses || []).map((status) => ({
      key: status,
      label: t(`crm.status.${status}`),
      count: counts[status] || 0,
    })),
    { key: 'all', label: t('crm.tabs.all'), count: total },
  ];

  return (
    <div className="crm-page">
      <header className="crm-head">
        <div>
          <h1 className="crm-title">{t('crm.leads.title')}</h1>
          <p className="crm-sub">{t('crm.leads.subtitle')}</p>
        </div>
        <div className="crm-head-actions">
          <button type="button" className="crm-btn" onClick={load} disabled={busy}>
            <RefreshCw size={15} className={busy ? 'crm-spin' : undefined} />
            {t('crm.refresh')}
          </button>
          <button type="button" className="crm-btn primary" onClick={() => setAdding(true)}>
            <Plus size={15} />
            {t('crm.leads.addManual')}
          </button>
        </div>
      </header>

      {error && <div className="crm-error">{String(error)}</div>}

      <div className="crm-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`crm-tab${view === tab.key ? ' on' : ''}${tab.alarm && tab.count ? ' alarm' : ''}`}
            onClick={() => setView(tab.key)}
          >
            {tab.alarm && <AlarmClock size={14} />}
            {tab.label}
            <span className="crm-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      <form className="crm-searchbar" onSubmit={submitSearch}>
        <Search size={15} />
        <input
          className="crm-search-input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('crm.leads.searchPlaceholder')}
        />
        <button type="submit" className="crm-btn">{t('crm.search')}</button>
      </form>

      {adding && meta && (
        <ManualLeadForm
          onClose={() => setAdding(false)}
          onCreated={async () => {
            setAdding(false);
            await load();
          }}
        />
      )}

      {data && data.leads.length === 0 && <p className="crm-empty">{t('crm.leads.empty')}</p>}

      {data && data.leads.length > 0 && (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>{t('crm.lead.who')}</th>
                <th>{t('crm.lead.waiting')}</th>
                <th>{t('crm.lead.status')}</th>
                <th>{t('crm.lead.reach')}</th>
              </tr>
            </thead>
            <tbody>
              {data.leads.map((lead) => {
                const overdue =
                  !lead.first_contacted_at && new Date(lead.callback_due_at) < new Date();
                const comeBack = meta && meta.come_back_statuses.includes(lead.status);
                return (
                  <tr key={lead.id} className={overdue ? 'crm-row-overdue' : undefined}>
                    <td>
                      <Link className="crm-link" to={`/crm/leads/${lead.id}`}>
                        {lead.name}
                      </Link>
                      <div className="crm-cell-sub">
                        {lead.phone ? maskedPhone(lead.phone) : lead.email}
                        {lead.phone?.is_landline && ` · ${t('crm.lead.landline')}`}
                      </div>
                      {comeBack && (
                        <div className="crm-cell-sub">
                          {snoozedUntil(lead) ? (
                            t('crm.lead.comeBackAt', { when: formatDateTime(lead.next_action_at) })
                          ) : (
                            <span className="crm-come-back-due">
                              <AlarmClock size={13} />
                              {t('crm.lead.comeBackDue')}
                              {lead.next_action && ` · ${lead.next_action}`}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {lead.first_contacted_at ? (
                        <span className="crm-cell-sub">
                          {t('crm.lead.answeredIn')} {formatMinutes(lead.minutes_to_first_contact, t)}
                        </span>
                      ) : (
                        <span className={overdue ? 'crm-overdue' : undefined}>
                          {formatAge(lead.created_at, t)}
                        </span>
                      )}
                      {overdue && <div className="crm-cell-sub">{t('crm.lead.overdue')}</div>}
                      {snoozedUntil(lead) && !comeBack && (
                        <div className="crm-cell-sub crm-snoozed">
                          {t('crm.lead.snoozedUntil', { when: formatDateTime(lead.next_action_at) })}
                        </div>
                      )}
                    </td>
                    <td>
                      {meta && (
                        <StatusPicker
                          statuses={meta.statuses}
                          comeBackStatuses={meta.come_back_statuses}
                          value={lead.status}
                          onMove={(body) => move(lead, body)}
                        />
                      )}
                    </td>
                    <td>
                      <GreetedContactButtons
                        record={lead}
                        fill={(id) => api.fillCrmLeadGreetingNames(id)}
                        greetingScript={meta?.greeting_script}
                        phone={lead.phone}
                        email={lead.email}
                        size="compact"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > PAGE_SIZE && (
        <div className="crm-pager">
          <button type="button" className="crm-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>
            {t('crm.prev')}
          </button>
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)} / {data.total}
          </span>
          <button
            type="button"
            className="crm-btn"
            disabled={(page + 1) * PAGE_SIZE >= data.total}
            onClick={() => setPage(page + 1)}
          >
            {t('crm.next')}
          </button>
        </div>
      )}
    </div>
  );
}
